import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import styles from "./WalletTransactionsPanel.module.scss";
import { TransactionCard } from "../components/transaction-card/TransactionCard";
import { useLazyGetWalletTransactionsQuery } from "../../../../service/wallet-service";
import { WalletTransactionResponse } from "../models/WalletTransactionResponse";
import { formatCurrency } from "../../../../utils";
import { CountryCode, countryCurrencies } from "../../../../model/common/country";
import { RootState } from "../../../../stores/store";
import { ElectricityBillsQuickServiceIcon, TransactionArrowDownIcon, HistIcon } from "../../../../assets/icons";
import { ApplicationRoutes } from "../../../../routes";

const formatTxDate = (dateString: string): string => {
	const date = new Date(dateString);
	const datePart = date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
	const timePart = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }).toLowerCase();
	return `${datePart}, ${timePart}`;
};

const getIndicatorIcon = (indicator: string) => {
	if (indicator === "C") return <TransactionArrowDownIcon />;
	if (indicator === "D") return <TransactionArrowDownIcon style={{ transform: "rotate(180deg)" }} />;
	return <HistIcon />;
};

export const WalletTransactionsPanel: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const countryCode = useSelector((state: RootState) => state.user.countryCode);
	const currency = countryCurrencies[countryCode as CountryCode] ?? "NGN";
	const [getWalletTransactions, { data, isFetching }] = useLazyGetWalletTransactionsQuery();

	useEffect(() => {
		getWalletTransactions({ page: 1, pageSize: 5 });
	}, []);

	const transactions: WalletTransactionResponse[] = data?.items ?? [];

	const getAmountText = (transaction: WalletTransactionResponse) => {
		const amountText = formatCurrency(Math.abs(transaction.amount), { currency });
		if (transaction.debitCreditIndicator === "C") return `+${amountText}`;
		if (transaction.debitCreditIndicator === "D") return `-${amountText}`;
		return amountText;
	};

	return (
		<div className={styles.card}>
			<div className={styles.header}>
				<span className={styles.title}>{t("common.labels.walletTransactions")}</span>
				<button className={styles.seeMoreBtn} onClick={() => navigate(`/${ApplicationRoutes.CUSTOMER_WALLET_HISTORY}`)}>
					{t("wallet.web.seeMore")}
				</button>
			</div>

			<div className={styles.body}>
				{isFetching && transactions.length === 0 && (
					<div className={styles.loadingList}>
						{[1, 2, 3, 4, 5].map(i => (
							<div key={i} className={styles.skeletonRow} />
						))}
					</div>
				)}

				{!isFetching && transactions.length === 0 && (
					<div className={styles.emptyState}>
						<div className={styles.emptyIcon}>
							<ElectricityBillsQuickServiceIcon />
						</div>
						<p className={styles.emptyText}>{t("history.yourTransactionsWillAppearHere")}</p>
					</div>
				)}

				{transactions.map(tx => (
					<TransactionCard
						key={tx.id}
						icon={getIndicatorIcon(tx.debitCreditIndicator)}
						leftTitle={tx.remarks || ""}
						leftSubTitle={formatTxDate(tx.dateCreated)}
						rightTitle={getAmountText(tx)}
						status={t("common.labels.completed")}
					/>
				))}
			</div>
		</div>
	);
};
